<template>
  <div class="warehouse-page">
    <p v-if="error" class="banner error">{{ error }}</p>
    <p v-if="notice" class="banner ok">{{ notice }}</p>

    <div class="toolbar">
      <button class="btn" type="button" @click="openCreate">Add future source</button>
    </div>

    <div class="grid">
      <button
        v-for="source in sources"
        :key="source.code"
        type="button"
        class="source-card"
        :class="{ active: selected === source.code }"
        @click="selectSource(source.code)"
      >
        <div class="card-head">
          <strong>{{ source.name }}</strong>
          <span class="pill" :class="{ on: source.is_active }">{{ source.is_active ? 'Active' : 'Off' }}</span>
        </div>
        <p>{{ source.environment }} · {{ source.code }}</p>
        <p class="muted">{{ source.item_count }} items · mapping ready for ingest</p>
      </button>
    </div>

    <section v-if="mapping" class="card">
      <div class="card-head">
        <div>
          <h2>{{ mapping.source.name }}</h2>
          <p class="muted">Map inbound fields from this environment onto SECTOR warehouse parameters.</p>
        </div>
        <button class="btn ghost" type="button" @click="openEditSource">Edit source</button>
      </div>

      <table>
        <thead>
          <tr>
            <th>SECTOR parameter</th>
            <th>Group</th>
            <th>Source field</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="field in mapping.fields" :key="field.key">
            <td>
              {{ field.label }}
              <span v-if="field.required" class="req">required</span>
            </td>
            <td>{{ field.group }}</td>
            <td>
              <input v-model="field.sourceField" :placeholder="field.required ? 'Map this field' : 'Optional'">
            </td>
            <td>
              <input v-model="field.notes" placeholder="Mapping note">
            </td>
          </tr>
        </tbody>
      </table>
      <div class="actions">
        <button class="btn" type="button" @click="saveMappings">Save mapping</button>
      </div>
    </section>

    <div v-if="showModal" class="modal" @click.self="showModal = false">
      <div class="modal-card">
        <div class="modal-head">
          <h2>{{ sourceForm.id ? 'Edit source' : 'Add source' }}</h2>
          <button class="icon-btn" type="button" @click="showModal = false"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <form class="modal-body" @submit.prevent="saveSource">
          <label>Code <input v-model="sourceForm.code" required :disabled="Boolean(sourceForm.id)" placeholder="sap_future_lab"></label>
          <label>Name <input v-model="sourceForm.name" required></label>
          <label>Environment
            <select v-model="sourceForm.environment">
              <option value="hospital">hospital</option>
              <option value="pharmacy">pharmacy</option>
              <option value="clinic">clinic</option>
              <option value="custom">custom / future</option>
            </select>
          </label>
          <label>Endpoint URL <input v-model="sourceForm.endpoint_url" placeholder="https://..."></label>
          <label class="wide">Description <input v-model="sourceForm.description"></label>
          <label class="check"><input v-model="sourceForm.is_active" type="checkbox"> Active</label>
          <div class="modal-actions">
            <button class="btn ghost" type="button" @click="showModal = false">Cancel</button>
            <button class="btn" type="submit">Save source</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import warehouseApi from '@/utils/warehouseApi'

const error = ref('')
const notice = ref('')
const sources = ref([])
const selected = ref('')
const mapping = ref(null)
const showModal = ref(false)
const sourceForm = reactive({
  id: null,
  code: '',
  name: '',
  environment: 'custom',
  description: '',
  endpoint_url: '',
  is_active: true
})

async function loadSources() {
  const { data } = await warehouseApi.get('/api/warehouse/sources')
  sources.value = data.sources || []
  if (!selected.value && sources.value[0]) selected.value = sources.value[0].code
}

async function selectSource(code) {
  selected.value = code
  const { data } = await warehouseApi.get(`/api/warehouse/sources/${code}/mappings`)
  mapping.value = data
}

function openCreate() {
  Object.assign(sourceForm, {
    id: null,
    code: '',
    name: '',
    environment: 'custom',
    description: '',
    endpoint_url: '',
    is_active: true
  })
  showModal.value = true
}

function openEditSource() {
  const current = sources.value.find((row) => row.code === selected.value)
  if (!current) return
  Object.assign(sourceForm, {
    id: current.id,
    code: current.code,
    name: current.name,
    environment: current.environment,
    description: current.description || '',
    endpoint_url: current.endpoint_url || '',
    is_active: Boolean(current.is_active)
  })
  showModal.value = true
}

async function saveSource() {
  try {
    if (sourceForm.id) {
      await warehouseApi.put(`/api/warehouse/sources/${sourceForm.code}`, sourceForm)
    } else {
      await warehouseApi.post('/api/warehouse/sources', sourceForm)
    }
    showModal.value = false
    notice.value = 'Source saved. Default field map created — complete any missing source fields.'
    await loadSources()
    await selectSource(sourceForm.code)
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to save source.'
  }
}

async function saveMappings() {
  try {
    await warehouseApi.put(`/api/warehouse/sources/${selected.value}/mappings`, {
      mappings: mapping.value.fields.map((field) => ({
        sectorField: field.key,
        sourceField: field.sourceField,
        notes: field.notes
      }))
    })
    notice.value = 'Field mapping saved. Inbound sync will use these names.'
    await selectSource(selected.value)
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to save mapping.'
  }
}

onMounted(async () => {
  try {
    await loadSources()
    if (selected.value) await selectSource(selected.value)
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to load sources.'
  }
})
</script>

<style scoped>
.toolbar { margin-bottom: 12px; }
.grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; margin-bottom: 16px; }
.source-card, .card {
  background: white; border: 1px solid #e2e8f0; border-radius: 8px; text-align: left;
}
.source-card { padding: 14px; cursor: pointer; }
.source-card.active { border-color: #0ea5e9; box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.15); }
.card { padding: 16px; }
.card-head { display: flex; justify-content: space-between; gap: 8px; align-items: flex-start; }
h2 { margin: 0; font-size: 1rem; color: #1e293b; }
.muted, .source-card p { margin: 4px 0 0; color: #64748b; font-size: 0.75rem; }
.pill { font-size: 0.7rem; padding: 2px 8px; border-radius: 999px; background: #fee2e2; color: #991b1b; }
.pill.on { background: #dcfce7; color: #166534; }
.req { margin-left: 6px; font-size: 0.68rem; color: #b45309; }
table { width: 100%; border-collapse: collapse; margin-top: 12px; }
th, td { padding: 8px; border-bottom: 1px solid #e2e8f0; font-size: 0.78rem; text-align: left; }
th { color: #475569; }
td input, .modal-body input, .modal-body select, .btn {
  padding: 8px 10px; border: 1px solid #e2e8f0; border-radius: 6px; width: 100%; font-size: 0.813rem; background: white;
}
.btn { background: #0ea5e9; color: white; border-color: #0ea5e9; cursor: pointer; width: auto; }
.btn.ghost { background: white; color: #475569; }
.actions { margin-top: 12px; }
.banner { padding: 10px 12px; border-radius: 6px; margin-bottom: 12px; font-size: 0.813rem; }
.banner.error { background: #fef2f2; color: #b91c1c; }
.banner.ok { background: #ecfdf5; color: #047857; }
.modal { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.4); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 16px; }
.modal-card { background: white; border-radius: 12px; width: min(640px, 100%); }
.modal-head, .modal-body { padding: 16px 20px; }
.modal-head { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #e2e8f0; }
.modal-head h2 { margin: 0; }
.modal-body { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.modal-body label { display: flex; flex-direction: column; gap: 4px; font-size: 0.75rem; color: #64748b; }
.modal-body .wide, .modal-body .check, .modal-actions { grid-column: 1 / -1; }
.modal-body .check { flex-direction: row; align-items: center; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; }
.icon-btn { border: none; background: transparent; cursor: pointer; color: #64748b; }
@media (max-width: 768px) { .modal-body { grid-template-columns: 1fr; } }
</style>
